import { ITeam, Team } from '@app/models/Team'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ITeamsListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<ITeam>
  readonly totalItems: number
}

const TeamsListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Team>(),
  totalItems: 0,
})

export class TeamsListState extends TeamsListStateRecord
  implements ITeamsListState {
  public static fromJS(initialState?: Partial<ITeamsListState>) {
    if (!initialState) {
      return new TeamsListState()
    }

    const { items, ...rest } = initialState
    return new TeamsListState({
      items: items ? List(items.map((i) => Team.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Team>
  public readonly totalItems: number

  constructor(props?: Partial<TeamsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum TeamsListActionTypes {
  LoadTeams = 'TEAMS_LIST::LOAD_TEAMS',
  LoadTeamsFailure = 'TEAMS_LIST::LOAD_TEAMS_FAILURE',
  LoadTeamsRequest = 'TEAMS_LIST::LOAD_TEAMS_REQUEST',
  LoadTeamsSuccess = 'TEAMS_LIST::LOAD_TEAMS_SUCCESS',
  SetConnectionType = 'TEAMS_LIST::SET_CONNECTION_TYPE',
  SetFilterText = 'TEAMS_LIST::SET_FILTER_TEXT',
  SetScope = 'TEAMS_LIST::SET_SCOPE',
  SetSortOrder = 'TEAMS_LIST::SET_SORT_ORDER',
}

interface ILoadTeamsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: TeamsListActionTypes.LoadTeams
}

interface ILoadTeamsFailureAction {
  error: string
  type: TeamsListActionTypes.LoadTeamsFailure
}

interface ILoadTeamsRequestAction {
  type: TeamsListActionTypes.LoadTeamsRequest
}

interface ILoadTeamsSuccessAction {
  append: boolean
  items: Team[]
  totalItems: number
  type: TeamsListActionTypes.LoadTeamsSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: TeamsListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadTeamsFailureAction
  | ILoadTeamsRequestAction
  | ILoadTeamsSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadTeams(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadTeamsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: TeamsListActionTypes.LoadTeams,
  }
}

function loadTeamsFailure(error: string): ILoadTeamsFailureAction {
  return {
    error,
    type: TeamsListActionTypes.LoadTeamsFailure,
  }
}

function loadTeamsRequest(): ILoadTeamsRequestAction {
  return {
    type: TeamsListActionTypes.LoadTeamsRequest,
  }
}

function loadTeamsSuccess(
  items: Team[],
  append: boolean,
  totalItems: number,
): ILoadTeamsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: TeamsListActionTypes.LoadTeamsSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: TeamsListActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadTeams,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<TeamsListState> = (
  state: TeamsListState = new TeamsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case TeamsListActionTypes.LoadTeamsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as TeamsListState

    case TeamsListActionTypes.LoadTeamsRequest:
      return state.set('isLoading', true) as TeamsListState

    case TeamsListActionTypes.LoadTeamsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Team>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as TeamsListState

    case TeamsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as TeamsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadTeamsSaga(action: ILoadTeamsAction) {
  try {
    yield put(loadTeamsRequest())

    const currentState: TeamsListState = yield select(
      (state: ApplicationState) => state.teamsList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    query.push(`sort=Alphabetical`)

    const baseUrl = '/api/Teams'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Team[] = result.results.map((c: any) => Team.fromJS(c))
      yield put(loadTeamsSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadTeamsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadTeamsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadTeams() {
  yield takeLatest(TeamsListActionTypes.LoadTeams, loadTeamsSaga)
}

export function* teamsListRootSaga() {
  yield fork(watchLoadTeams)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): TeamsListState =>
  state.teamsList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
